/**
 * Inter-planet jumps: pick a destination (command, objective, escape,
 * personality), route multi-hop through the transit graph, then find a
 * launch that lands there (graph-cached fine-tune, line drive, sweeps).
 */

import { knownEnemies } from './targeting';
import type { PlayerState, PlanetState, GameState, BotConfig, TransitGraph, Vec3, BotFogContext } from '../../types';
import { hasUnlimitedJumpFuel } from '../../core/modeRules';
import type { BotJumpResult } from './types';
import { vecLength, vecSub, vecNormalize, vecDot, vecScale, vecAdd, surfaceTangentFrame } from '../../math';
import { JUMP_MIN_COST, JUMP_VELOCITY_PER_MOVEMENT, JUMPPACK_VELOCITY_BONUS, PHYS_DT, OUT_OF_BOUNDS } from '../../constants';
import { jumpVelocityBonusOf } from '../../core/actions';
import { sneakingNow } from './sneak';
import { eyePointAt } from '../../core/los';
import { knownGuardsOn, exposureAt } from './exposure';
import { computeLaunchDirection } from '../../core/gameState';
import { simulateJumpTrajectory, landingDamageFor, fallDamageMultiplierFor } from '../../core/physics';
import type { SeededRandom } from '../../core/random';
import { isWasmReady, wasmSimulateJumpBatch } from '../wasmPhysics';
import { findTransitPath } from '../transitGraph';
import { getBotObjectiveTarget } from '../botModeAI';
import { getPlayerWorldPos, getAliveEnemies } from './common';
import { tuningFor } from './tuning';
import { datan2 } from '../../core/dmath';

/**
 * Compute jump parameters for inter-planet travel.
 * When transitGraph is provided, uses cached reachability data and multi-hop pathfinding.
 */
export function computeBotJump(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  transitGraph?: TransitGraph,
  /** Phase 57: jump here (a guard reinforcing an alert planet) instead of choosing a target. */
  forcedTargetPlanetIndex?: number,
  /** Phase 57.6: what the bot knows (a sneaking jump lands away from the guards it can see or remembers). */
  fogContext?: BotFogContext,
): BotJumpResult | null {
  // K3: the jump planet choice reads only known enemies — 37.20 ledger #3 (teammates are always known)
  const enemies = knownEnemies(player, gameState, fogContext);
  const knownOn = (pIdx: number): boolean =>
    gameState.players.some(pl => pl.alive && pl.teamIndex === player.teamIndex && pl.planetIndex === pIdx && pl.id !== player.id)
    || enemies.some(e => e.planetIndex === pIdx);
  const { personality, skill } = config;
  const t = tuningFor(config).jumping;
  const planets = gameState.planets;
  // Outside plain deathmatch, fuel is unlimited (always apply velocity bonus)
  const hasFuel = player.jumppackFuel > 0 || hasUnlimitedJumpFuel(gameState.config.modeConfig);
  // Fuel and the kit's jump bonus (thrusters / jump trait) add to every launch exactly as jumpLaunchVector does —
  // a search that ignored them would overshoot every jump of a geared unit (Phase 48).
  const extraVel = (hasFuel ? JUMPPACK_VELOCITY_BONUS : 0) + jumpVelocityBonusOf(player);
  // Reach (37.12): a candidate must land within maxFlightSeconds — the sim simply stops there, so a longer flight is no hit
  const jumpSimSteps = Math.max(1, Math.round(t.maxFlightSeconds / PHYS_DT));

  // --- Landing judgement (37.13) -------------------------------------------
  // With the default tuning nothing here runs and the first trajectory that
  // reaches the target is taken, exactly as before. When a bot's tuning caps
  // landing damage (or prefers softer landings), each hit a search phase finds
  // is re-flown in JS to read its impact speed, and the hits are filtered /
  // ordered by the damage the landing would cost.
  type JumpParamBase = { az: number; elev: number; pow: number };
  // Sneaking (57.6): the landing spot matters as much as the landing speed — every hit is judged by how
  // exposed it would be to the guards the bot knows about on the target planet, and the least exposed wins.
  const sneakLanding = sneakingNow(gameState, player, config);
  const judgeLandings = sneakLanding || Number.isFinite(t.maxLandingHealthFraction) || Number.isFinite(t.maxLandingDamage) || t.preferSofterLanding;
  const landingCandidateCount = Math.max(1, sneakLanding ? Math.max(t.landingCandidates, 16) : t.landingCandidates);
  const jumpFallMult = fallDamageMultiplierFor('jump', gameState.config.fallDamageMultiplier ?? 1.0, gameState.config.fallDamageByCause);
  const judgeNormal = vecNormalize(player.surfaceDir);
  const judgeLaunchPos = vecAdd(getPlayerWorldPos(player, planets), vecScale(judgeNormal, 0.5));
  const judgeLanding = (p: JumpParamBase, targetIdx: number): { damage: number; exposure: number } => {
    const dir = computeLaunchDirection(judgeNormal, p.az, p.elev);
    let speed = (p.pow / 100) * player.movementRemaining * JUMP_VELOCITY_PER_MOVEMENT;
    speed += extraVel;
    const sim = simulateJumpTrajectory(judgeLaunchPos, vecScale(dir, speed), planets, jumpSimSteps, PHYS_DT);
    const damage = sim.landingSpeed === null ? Infinity : landingDamageFor(sim.landingSpeed, jumpFallMult);
    let exposure = 0;
    if (sneakLanding && sim.landingPoint && sim.landingPlanetIndex !== null && sim.landingPlanetIndex === targetIdx) {
      const target = planets[targetIdx];
      const eye = eyePointAt(target, vecNormalize(vecSub(sim.landingPoint, target.position)));
      exposure = exposureAt(eye, knownGuardsOn(player, gameState, targetIdx, fogContext), planets);
    }
    return { damage, exposure };
  };
  // 57.10b: a sneaking landing also keeps the reserve for the jump back (stealth.landingHealthFraction)
  const healthFraction = sneakLanding ? Math.min(t.maxLandingHealthFraction, tuningFor(config).stealth.landingHealthFraction) : t.maxLandingHealthFraction;
  const landingAcceptable = (dmg: number): boolean =>
    dmg <= t.maxLandingDamage && dmg < player.health * healthFraction;
  /** Which of a phase's hits to take: the first (default); the first / softest acceptable one; sneaking, the least exposed acceptable one. */
  const chooseHit = <P extends JumpParamBase>(hits: P[], targetIdx: number): { hit: P; damage?: number; exposure?: number } | null => {
    if (hits.length === 0) return null;
    if (!judgeLandings) return { hit: hits[0] };
    let best: { hit: P; damage: number; exposure: number } | null = null;
    for (const hit of hits.slice(0, landingCandidateCount)) {
      const j = judgeLanding(hit, targetIdx);
      if (!landingAcceptable(j.damage)) continue;
      if (!sneakLanding && !t.preferSofterLanding) return { hit, damage: j.damage };
      const better = !best
        || (sneakLanding && (j.exposure < best.exposure || (j.exposure === best.exposure && j.damage < best.damage)))
        || (!sneakLanding && j.damage < best.damage);
      if (better) best = { hit, damage: j.damage, exposure: j.exposure };
    }
    return best;
  };
  const asResult = (c: { hit: JumpParamBase; damage?: number; exposure?: number }): BotJumpResult => ({
    azimuth: c.hit.az, elevation: c.hit.elev, power: c.hit.pow,
    ...(c.damage !== undefined && (judgeLandings || t.preferSofterLanding || Number.isFinite(t.maxLandingDamage) || Number.isFinite(t.maxLandingHealthFraction)) ? { predictedLandingDamage: c.damage } : {}),
    ...(sneakLanding && c.exposure !== undefined ? { predictedLandingExposure: c.exposure } : {}),
  });

  // Pick target planet
  const spawnablePlanets = planets.filter(p => p.type === 'planet' && p.alive);
  if (spawnablePlanets.length <= 1) return null;

  let targetPlanet: PlanetState | null = null;
  let targetPlanetIndex = -1;

  if (forcedTargetPlanetIndex !== undefined && planets[forcedTargetPlanetIndex]?.alive && forcedTargetPlanetIndex !== player.planetIndex) { targetPlanet = planets[forcedTargetPlanetIndex]; targetPlanetIndex = forcedTargetPlanetIndex; }

  // Command override: moveToPlanet → jump to the commanded planet
  const cmd = player.pendingCommand;
  if (cmd?.type === 'moveToPlanet' && cmd.targetPlanetIndex !== undefined) {
    const cmdPlanet = planets[cmd.targetPlanetIndex];
    if (cmdPlanet && cmdPlanet.alive && cmdPlanet.type === 'planet'
        && cmd.targetPlanetIndex !== player.planetIndex) {
      targetPlanet = cmdPlanet;
      targetPlanetIndex = cmd.targetPlanetIndex;
    }
  }

  // Objective target: mode-specific destination (priority 2 after command)
  if (!targetPlanet) {
    const objTarget = getBotObjectiveTarget(player, gameState);
    if (objTarget && objTarget.planetIndex !== player.planetIndex) {
      const objPlanet = planets[objTarget.planetIndex];
      if (objPlanet && objPlanet.alive && objPlanet.type === 'planet') {
        const canJump = !(gameState.modeState.ctf
          && gameState.config.modeConfig.carrierCanJump === false
          && gameState.carryables.some(c => c.carriedBy === player.id));
        if (canJump) {
          targetPlanet = objPlanet;
          targetPlanetIndex = objTarget.planetIndex;
        }
      }
    }
  }

  // Survival escape: low health or heavily damaged planet → prefer empty planets
  const currentPlanet = planets[player.planetIndex];
  const planetDamage = 1 - currentPlanet.mass / currentPlanet.initialMass;
  if (!targetPlanet && (player.health < t.escapeHealthBelow || planetDamage > t.escapePlanetDamageAbove)) {
    for (const p of spawnablePlanets) {
      const pIdx = planets.indexOf(p);
      if (pIdx === player.planetIndex) continue;
      const occupied = knownOn(pIdx);
      if (!occupied) { targetPlanet = p; targetPlanetIndex = pIdx; break; }
    }
  }

  if (!targetPlanet && personality.aggression > t.aggressiveAbove) {
    // Jump to planet with most enemies
    let bestCount = 0;
    for (const p of spawnablePlanets) {
      if (p.id === planets[player.planetIndex].id) continue;
      const pIdx = planets.indexOf(p);
      const count = enemies.filter(e => e.planetIndex === pIdx).length;
      if (count > bestCount) { bestCount = count; targetPlanet = p; targetPlanetIndex = pIdx; }
    }
  } else if (!targetPlanet && personality.aggression < t.defensiveBelow) {
    // Jump to empty planet (escape)
    for (const p of spawnablePlanets) {
      const pIdx = planets.indexOf(p);
      if (pIdx === player.planetIndex) continue;
      const occupied = knownOn(pIdx);
      if (!occupied) { targetPlanet = p; targetPlanetIndex = pIdx; break; }
    }
  }

  if (!targetPlanet) {
    // Pick random other planet
    const others = spawnablePlanets.filter((_, i) => {
      const realIdx = planets.indexOf(spawnablePlanets[i]);
      return realIdx !== player.planetIndex;
    });
    if (others.length === 0) return null;
    const pick = others[rng.int(0, others.length - 1)];
    targetPlanet = pick;
    targetPlanetIndex = planets.indexOf(pick);
  }

  // Multi-hop: if target isn't directly reachable, use pathfinding to find intermediate hop
  let effectiveTargetIndex = targetPlanetIndex;
  if (transitGraph) {
    const directEntries = transitGraph.get(player.planetIndex);
    const directlyReachable = directEntries?.some(e => e.targetPlanetIndex === targetPlanetIndex);
    if (!directlyReachable) {
      // Route with the velocity the unit will really launch at (fuel + kit bonus), no further than the bot's reach allows
      const path = findTransitPath(transitGraph, player.planetIndex, targetPlanetIndex, planets, player.movementRemaining, extraVel, t.maxHops);
      if (path && path.length >= 2 && path.length - 1 <= t.maxHops) {
        // Jump to the first intermediate hop
        effectiveTargetIndex = path[1];
      }
    }
  }

  const effectiveTarget = planets[effectiveTargetIndex];

  // --- Graph-assisted aim: use cached params if available ---
  if (transitGraph) {
    const entries = transitGraph.get(player.planetIndex);
    const cached = entries?.find(e => e.targetPlanetIndex === effectiveTargetIndex);
    if (cached) {
      // Compute azimuth from bot's actual position toward target
      const myPos = getPlayerWorldPos(player, gameState.planets);
      const normal = vecNormalize(player.surfaceDir);
      const toTarget = vecNormalize(vecSub(effectiveTarget.position, myPos));
      const tangentComponent = vecSub(toTarget, vecScale(normal, vecDot(toTarget, normal)));
      let azimuth = 0;
      if (vecLength(tangentComponent) > 0.001) {
        const { tx, ty } = surfaceTangentFrame(normal);
        azimuth = datan2(vecDot(tangentComponent, ty), vecDot(tangentComponent, tx));
      }

      // Fine-tune: sweep azimuth × elevation × power around cached params
      const azOffsets = [-0.4, -0.2, 0, 0.2, 0.4];
      const elevOffsets = [-0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3];
      const powOffsets = [20, 10, 0, -10, -20];
      const surfaceNormal = vecNormalize(player.surfaceDir);
      const graphLaunchPos = vecAdd(myPos, vecScale(surfaceNormal, 0.5));

      // Batch all candidates: 5×7×5 = 175 sims
      type GraphParam = { az: number; elev: number; pow: number };
      const graphParams: GraphParam[] = [];
      for (const aOff of azOffsets) {
        const az = azimuth + aOff;
        for (const eOff of elevOffsets) {
          for (const pOff of powOffsets) {
            const elev = Math.max(0.2, Math.min(1.3, cached.elevation + eOff));
            const pow = Math.max(20, Math.min(95, cached.power + pOff));
            const movementToUse = (pow / 100) * player.movementRemaining;
            if (movementToUse < JUMP_MIN_COST) continue;
            graphParams.push({ az, elev, pow });
          }
        }
      }

      // Try WASM batch: every candidate that reaches the target, in search order
      const graphHits: GraphParam[] = [];
      let wasmTried = false;

      if (isWasmReady() && graphParams.length > 0) {
        const trajectories = graphParams.map(p => {
          const dir = computeLaunchDirection(surfaceNormal, p.az, p.elev);
          let vel = (p.pow / 100) * player.movementRemaining * JUMP_VELOCITY_PER_MOVEMENT;
          vel += extraVel;
          return { pos: graphLaunchPos, vel: vecScale(dir, vel) };
        });
        const wasmResults = wasmSimulateJumpBatch(trajectories, effectiveTargetIndex, jumpSimSteps, OUT_OF_BOUNDS, PHYS_DT);
        if (wasmResults) {
          wasmTried = true;
          for (let i = 0; i < wasmResults.length; i++) {
            if (wasmResults[i].closestApproach === 0) {
              graphHits.push(graphParams[i]);
              if (!judgeLandings) break; // first hit is the answer, as before
            }
          }
        }
      }

      // JS fallback if WASM unavailable or returned null
      if (!wasmTried && !isWasmReady()) {
        for (const p of graphParams) {
          const launchDir = computeLaunchDirection(surfaceNormal, p.az, p.elev);
          let velocity = (p.pow / 100) * player.movementRemaining * JUMP_VELOCITY_PER_MOVEMENT;
          velocity += extraVel;
          const sim = simulateJumpTrajectory(graphLaunchPos, vecScale(launchDir, velocity), planets, jumpSimSteps, PHYS_DT);
          if (sim.landingPlanetIndex === effectiveTargetIndex) {
            graphHits.push(p);
            if (!judgeLandings || graphHits.length >= t.landingCandidates) break;
          }
        }
      }

      const chosen = chooseHit(graphHits, effectiveTargetIndex);
      if (chosen) return asResult(chosen);
    }
  }

  // --- Fallback: line drive + comprehensive sweep ---
  // Uses WASM batch simulation when available (3-8x faster than JS).
  // Each phase generates candidate params, batches them through WASM, checks for hits.
  const myPos = getPlayerWorldPos(player, gameState.planets);
  const surfaceNormal = vecNormalize(player.surfaceDir);
  const launchPos = vecAdd(myPos, vecScale(surfaceNormal, 0.5));
  const targetR = effectiveTarget.radius;
  const useWasm = isWasmReady();

  // Helper: azimuth from surface toward a world position
  function jumpAzimuthTo(worldPos: Vec3): number {
    const toPos = vecNormalize(vecSub(worldPos, myPos));
    const tang = vecSub(toPos, vecScale(surfaceNormal, vecDot(toPos, surfaceNormal)));
    if (vecLength(tang) < 0.001) return 0;
    const { tx: ttx, ty: tty } = surfaceTangentFrame(surfaceNormal);
    return datan2(vecDot(tang, tty), vecDot(tang, ttx));
  }

  type JumpParam = { az: number; elev: number; pow: number };
  type JumpCandidate = JumpParam & { closestApproach: number };

  // Convert param to trajectory for WASM
  function paramToTrajectory(p: JumpParam): { pos: Vec3; vel: Vec3 } {
    const dir = computeLaunchDirection(surfaceNormal, p.az, p.elev);
    let vel = (p.pow / 100) * player.movementRemaining * JUMP_VELOCITY_PER_MOVEMENT;
    vel += extraVel;
    return { pos: launchPos, vel: vecScale(dir, vel) };
  }

  // Batch simulate: returns candidates with closestApproach filled in, and the
  // params that reach the target in search order (just the first one unless
  // landings are being judged)
  function batchJumpSim(params: JumpParam[]): { candidates: JumpCandidate[]; hits: JumpParam[] } {
    // Filter out invalid params (movement too low)
    const valid: { p: JumpParam; origIdx: number }[] = [];
    const candidates: JumpCandidate[] = params.map(p => ({ ...p, closestApproach: Infinity }));
    for (let i = 0; i < params.length; i++) {
      const movementToUse = (params[i].pow / 100) * player.movementRemaining;
      if (movementToUse >= JUMP_MIN_COST) {
        valid.push({ p: params[i], origIdx: i });
      }
    }
    const hits: JumpParam[] = [];
    if (valid.length === 0) return { candidates, hits };

    if (useWasm) {
      const trajectories = valid.map(v => paramToTrajectory(v.p));
      const wasmResults = wasmSimulateJumpBatch(trajectories, effectiveTargetIndex, jumpSimSteps, OUT_OF_BOUNDS, PHYS_DT);
      if (wasmResults) {
        for (let j = 0; j < valid.length; j++) {
          const idx = valid[j].origIdx;
          candidates[idx] = { ...params[idx], closestApproach: wasmResults[j].closestApproach };
          if (wasmResults[j].closestApproach === 0) hits.push(params[idx]);
        }
        return { candidates, hits };
      }
      // WASM returned null — fall through to JS
    }

    // JS fallback
    for (const v of valid) {
      const p = v.p;
      const traj = paramToTrajectory(p);
      const sim = simulateJumpTrajectory(launchPos, traj.vel, planets, jumpSimSteps, PHYS_DT);
      if (sim.landingPlanetIndex === effectiveTargetIndex) {
        candidates[v.origIdx] = { ...p, closestApproach: 0 };
        hits.push(p);
        // JS path: stop at the first hit unless landings are being judged
        if (!judgeLandings || hits.length >= t.landingCandidates) break;
        continue;
      }
      // Closest approach from trajectory points (sample every 20th)
      let closest = Infinity;
      const pts = sim.points;
      for (let i = 0; i < pts.length; i += 20) {
        const d = vecLength(vecSub(pts[i], effectiveTarget.position)) - targetR;
        if (d < closest) closest = d;
      }
      candidates[v.origIdx] = { ...p, closestApproach: closest };
    }
    return { candidates, hits };
  }

  // === Phase 0: Line drive — aim at target, max power, sweep elevation ===
  {
    const phase0Params: JumpParam[] = [];
    const directAz = jumpAzimuthTo(effectiveTarget.position);
    for (const azOff of [-0.15, -0.05, 0, 0.05, 0.15]) {
      for (const elev of [0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1.05, 1.2]) {
        for (const pow of [98, 90, 80]) {
          phase0Params.push({ az: directAz + azOff, elev, pow });
        }
      }
    }
    const chosen0 = chooseHit(batchJumpSim(phase0Params).hits, effectiveTargetIndex);
    if (chosen0) return asResult(chosen0);
  }

  // === Phase 1: Coarse sweep (all sub-phases batched together) ===
  const phase1Params: JumpParam[] = [];

  // Phase 1a: Full 360° coarse — 24×8×8 = 1536 (reach: fullSweep)
  for (let ai = 0; ai < (t.fullSweep ? 24 : 0); ai++) {
    const az = (Math.PI * 2 * ai) / 24;
    for (let ei = 0; ei < 8; ei++) {
      const elev = 0.15 + (1.2 * ei) / 7;
      for (let pi = 0; pi < 8; pi++) {
        const pow = 20 + (75 * pi) / 7;
        phase1Params.push({ az, elev, pow });
      }
    }
  }

  // Phase 1b: Gravity assist seeds — use 5 closest bodies to avoid O(N) scaling
  const gravityBodies: Array<{ idx: number; az: number; ratio: number }> = [];
  for (let i = 0; i < planets.length; i++) {
    if (i === player.planetIndex || i === effectiveTargetIndex) continue;
    const body = planets[i];
    if (!body.alive) continue;
    const bodyDist = vecLength(vecSub(body.position, myPos));
    gravityBodies.push({ idx: i, az: jumpAzimuthTo(body.position), ratio: Math.min(bodyDist / 80, 1) });
  }
  gravityBodies.sort((a, b) => a.ratio - b.ratio);
  for (const body of gravityBodies.slice(0, t.gravityAssist ? 5 : 0)) {
    for (const azOff of [-0.3, -0.15, 0, 0.15, 0.3]) {
      for (const [e, p] of [[0.3 + body.ratio * 0.4, 15 + body.ratio * 60], [0.5 + body.ratio * 0.3, 25 + body.ratio * 45]] as [number, number][]) {
        phase1Params.push({ az: body.az + azOff, elev: e, pow: p });
      }
    }
  }

  // Phase 1c: Direct-to-target focused seeds — 7×6×4 = 168
  const targetAz = jumpAzimuthTo(effectiveTarget.position);
  for (const azOff of [-0.5, -0.3, -0.15, 0, 0.15, 0.3, 0.5]) {
    for (const elev of [0.2, 0.4, 0.6, 0.8, 1.0, 1.2]) {
      for (const pow of [40, 60, 80, 95]) {
        phase1Params.push({ az: targetAz + azOff, elev, pow });
      }
    }
  }

  // Phase 1d: Random exploration seeds — 40 random per turn (reach: fullSweep)
  for (let i = 0; i < (t.fullSweep ? 40 : 0); i++) {
    phase1Params.push({
      az: rng.next() * Math.PI * 2,
      elev: 0.1 + rng.next() * 1.3,
      pow: 20 + rng.next() * 78,
    });
  }

  // Batch all Phase 1 candidates at once
  const { candidates: coarseResults, hits: phase1Hits } = batchJumpSim(phase1Params);
  const chosen1 = chooseHit(phase1Hits, effectiveTargetIndex);
  if (chosen1) return asResult(chosen1);

  // === Phase 2: Focused refinement around near-misses ===
  const sorted = coarseResults.filter(c => c.closestApproach < Infinity);
  sorted.sort((a, b) => a.closestApproach - b.closestApproach);
  const topNearMisses = sorted.slice(0, 5);

  // Batch all refinement candidates together
  const phase2Params: JumpParam[] = [];
  for (const seed of topNearMisses) {
    if (seed.closestApproach > targetR * 10) continue;
    const azRange = 0.3;
    const elevRange = 0.4;
    const powRange = 20;
    for (let ai = 0; ai < 7; ai++) {
      const az = seed.az - azRange / 2 + (azRange * ai) / 6;
      for (let ei = 0; ei < 7; ei++) {
        const elev = Math.max(0.1, Math.min(1.4, seed.elev - elevRange / 2 + (elevRange * ei) / 6));
        for (let pi = 0; pi < 7; pi++) {
          const pow = Math.max(20, Math.min(98, seed.pow - powRange / 2 + (powRange * pi) / 6));
          phase2Params.push({ az, elev, pow });
        }
      }
    }
  }

  if (phase2Params.length > 0 && t.refineNearMisses) {
    const chosen2 = chooseHit(batchJumpSim(phase2Params).hits, effectiveTargetIndex);
    if (chosen2) return asResult(chosen2);
  }

  // Target planet unreachable — return null instead of accepting random landing.
  // The plan executor will fall through to the next step (walk toward objective).
  return null;
}
